<script lang="ts">
  import Header from '../../app/layout/Header.svelte';
  import { themeMode, type ThemeMode } from '../../app/theme/theme';
  import { exportAllToCsv, importFromCsv } from '../../data/csv';

  let exporting = $state(false);
  let exportError = $state<string | null>(null);

  let importing = $state(false);
  let importError = $state<string | null>(null);
  let importResultMessage = $state<string | null>(null);

  let showImportConfirm = $state(false);
  let pendingFile = $state<File | null>(null);
  let fileInputEl = $state<HTMLInputElement | undefined>(undefined);

  const themeOptions: { value: ThemeMode; label: string }[] = [
    { value: 'system', label: 'システム設定に合わせる' },
    { value: 'light', label: 'ライト' },
    { value: 'dark', label: 'ダーク' },
  ];

  function selectTheme(mode: ThemeMode) {
    $themeMode = mode;
  }

  function pad2(n: number): string {
    return String(n).padStart(2, '0');
  }

  function todayFileDate(): string {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  }

  async function handleExport() {
    exportError = null;
    exporting = true;
    try {
      const csv = await exportAllToCsv();
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expense-tracker-backup-${todayFileDate()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      exportError = 'エクスポートに失敗しました。';
      console.error(err);
    } finally {
      exporting = false;
    }
  }

  function handleFileChange(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    importError = null;
    importResultMessage = null;
    pendingFile = file;
    showImportConfirm = true;
  }

  function cancelImport() {
    showImportConfirm = false;
    pendingFile = null;
    if (fileInputEl) fileInputEl.value = '';
  }

  async function confirmImport() {
    const file = pendingFile;
    showImportConfirm = false;
    if (!file) return;

    importing = true;
    importError = null;
    importResultMessage = null;
    try {
      const text = await file.text();
      const result = await importFromCsv(text);
      importResultMessage = `インポートが完了しました（カテゴリ ${result.categories}件、支出 ${result.expenses}件、予算 ${result.budgets}件）。`;
    } catch (err) {
      importError = 'インポートに失敗しました。ファイルの内容を確認してください。';
      console.error(err);
    } finally {
      importing = false;
      pendingFile = null;
      if (fileInputEl) fileInputEl.value = '';
    }
  }
</script>

<Header title="設定" showBack={true} backTo="/" />

<main class="settings-page">
  <section class="settings-section">
    <h2 class="settings-section__title">表示テーマ</h2>
    <div class="theme-options" role="radiogroup" aria-label="表示テーマ">
      {#each themeOptions as option (option.value)}
        <label class="theme-option">
          <input
            type="radio"
            name="theme-mode"
            value={option.value}
            checked={$themeMode === option.value}
            onchange={() => selectTheme(option.value)}
          />
          <span>{option.label}</span>
        </label>
      {/each}
    </div>
  </section>

  <section class="settings-section">
    <h2 class="settings-section__title">データのバックアップ</h2>
    <p class="settings-section__desc">
      全カテゴリ・支出・予算のデータをCSVファイルとして書き出します。
    </p>
    <button type="button" class="btn btn--primary" onclick={handleExport} disabled={exporting}>
      {exporting ? '書き出し中…' : 'CSVをエクスポート'}
    </button>
    {#if exportError}
      <p class="settings-message settings-message--error">{exportError}</p>
    {/if}
  </section>

  <section class="settings-section">
    <h2 class="settings-section__title">データの復元</h2>
    <p class="settings-section__desc">
      エクスポートしたCSVファイルから全データを復元します。現在のデータはすべて削除されて置き換わります。
    </p>
    <label class="btn btn--secondary file-input-label">
      CSVをインポート
      <input
        bind:this={fileInputEl}
        type="file"
        accept=".csv"
        class="file-input"
        onchange={handleFileChange}
        disabled={importing}
      />
    </label>
    {#if importing}
      <p class="settings-message">インポート中…</p>
    {/if}
    {#if importResultMessage}
      <p class="settings-message settings-message--success">{importResultMessage}</p>
    {/if}
    {#if importError}
      <p class="settings-message settings-message--error">{importError}</p>
    {/if}
  </section>

  <section class="settings-section">
    <h2 class="settings-section__title">データの保存について</h2>
    <p class="settings-section__desc">
      このアプリのデータはこの端末（このブラウザ）内にのみ保存されます。1端末＝1データの構成であり、複数端末間の自動同期は行われません。他の端末に引き継ぐ場合は、CSVエクスポート／インポートをご利用ください。
    </p>
  </section>
</main>

{#if showImportConfirm}
  <div
    class="modal-overlay"
    role="presentation"
    onclick={cancelImport}
    onkeydown={(e) => e.key === 'Escape' && cancelImport()}
  >
    <div
      class="modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="import-confirm-title"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.stopPropagation()}
    >
      <h2 id="import-confirm-title" class="modal__title">インポートの確認</h2>
      <p class="modal__body">
        現在のデータが消去されます。事前にバックアップを取得してください。削除してよろしいですか？
      </p>
      <div class="modal__actions">
        <button type="button" class="btn btn--secondary" onclick={cancelImport}>キャンセル</button>
        <button type="button" class="btn btn--danger" onclick={confirmImport}>削除して実行</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .settings-page {
    max-width: 32rem;
    margin: 0 auto;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .settings-section {
    background-color: var(--color-bg-elevated);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: 1rem;
  }

  .settings-section__title {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .settings-section__desc {
    margin: 0 0 0.75rem;
    font-size: 0.875rem;
    color: var(--color-text-muted);
    line-height: 1.6;
  }

  .theme-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .theme-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.95rem;
    color: var(--color-text);
    cursor: pointer;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    border: none;
    border-radius: var(--radius-md);
    padding: 0.6rem 1rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn--primary {
    background-color: var(--color-primary);
    color: var(--color-primary-text);
  }

  .btn--secondary {
    background-color: var(--color-bg);
    color: var(--color-text);
    border: 1px solid var(--color-border);
  }

  .btn--danger {
    background-color: var(--color-danger);
    color: var(--color-primary-text);
  }

  .file-input-label {
    position: relative;
    overflow: hidden;
  }

  .file-input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
  }

  .settings-message {
    margin: 0.75rem 0 0;
    font-size: 0.875rem;
  }

  .settings-message--error {
    color: var(--color-danger);
  }

  .settings-message--success {
    color: var(--color-success);
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    background-color: var(--color-bg-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    z-index: 100;
  }

  .modal {
    background-color: var(--color-bg);
    color: var(--color-text);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: 1.25rem;
    max-width: 24rem;
    width: 100%;
    box-shadow: 0 10px 30px var(--color-shadow);
  }

  .modal__title {
    margin: 0 0 0.75rem;
    font-size: 1.05rem;
    font-weight: 700;
  }

  .modal__body {
    margin: 0 0 1.25rem;
    font-size: 0.9rem;
    line-height: 1.6;
    color: var(--color-text);
  }

  .modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
</style>
